import { apiClient } from './client';

/**
 * 사업장(지사) · 임직원 근무지 — 2026-08-30 · 판 K.
 *
 * ★★ 지사가 여럿이고 **주소가 다르다**(사용자 2026-08-30). 지금까지 주소는 회사에 하나뿐이라
 *    그 사실을 어디에도 적을 수 없었다.
 */

export interface WorksiteDto {
  id: string;
  name: string;
  code: string | null;
  postalCode: string | null;
  address: string | null;
  addressDetail: string | null;
  phone: string | null;
  /** 회사에 하나뿐. **근무지를 안 정한 사람이 속한 것으로 본다.** */
  isHeadOffice: boolean;
  /** 해외 지사의 벽시계. 비우면 회사 시간대. */
  timezone: string | null;
  isActive: boolean;
  sortOrder: number;
  headcount: number;
  /*
   * ── 사업장 v3 (2026-09-14 · 서버 feat/worksites-extend · 마이그 199) ──
   * 옛 서버는 이 칸들을 **보내지 않는다**(undefined) — 화면은 그때 「API 필요」 자리표시로 선다. `null` 은 «값이 없음».
   */
  managerUserId?: string | null;
  managerName?: string | null;
  /** "123-45-67890" */
  businessRegistrationNo?: string | null;
  /** 숫자 네 자리 "0001" */
  subBusinessNo?: string | null;
  /** 영문 두 자리 · null 이면 회사 기본(KR) 공휴일 */
  holidayCountryCode?: string | null;
  /** 후속(마이그 200) 전에는 늘 null */
  defaultWorkSchedule?: { scheduleId: string; name: string } | null;
  /** 그 사업장 인원의 팀별 집계 · 인원 많은 순 · 팀 없는 사람은 teamId/teamName null 한 줄 */
  teams?: WorksiteTeamCountDto[];
  updatedAt?: string;
  updatedBy?: string | null;
  updatedByName?: string | null;
}

export interface WorksiteTeamCountDto {
  teamId: string | null;
  teamName: string | null;
  count: number;
}

export interface WorksitePersonDto {
  userId: string;
  name: string;
  position: string | null;
  teamName: string | null;
  worksiteId: string | null;
  /** 사업장 v3 — 그 사업장에 배정된 날(서버가 주면). 옛 서버는 undefined. */
  worksiteSince?: string | null;
}

export async function listWorksites(): Promise<{
  worksites: WorksiteDto[]; unassigned: number; people: WorksitePersonDto[];
}> {
  const { data } = await apiClient.get('/hr/worksites');
  return data;
}

export interface WorksiteInput {
  name: string;
  code?: string | null;
  postalCode?: string | null;
  address?: string | null;
  addressDetail?: string | null;
  phone?: string | null;
  isHeadOffice?: boolean;
  timezone?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  /* 사업장 v3 — PATCH 에서 **안 보내면 유지**, null 을 보내야 비워진다(기존 칸은 보낸 값으로 통째 교체). */
  managerUserId?: string | null;
  businessRegistrationNo?: string | null;
  subBusinessNo?: string | null;
  holidayCountryCode?: string | null;
}

export async function createWorksite(body: WorksiteInput): Promise<{ id: string; name: string }> {
  const { data } = await apiClient.post('/hr/worksites', body);
  return data;
}

export async function updateWorksite(id: string, body: WorksiteInput): Promise<{ id: string }> {
  const { data } = await apiClient.patch(`/hr/worksites/${id}`, body);
  return data;
}

/** 지워도 **사람은 남는다** — 근무지만 비워지고, 몇 명이 비워졌는지 돌려준다. */
export async function deleteWorksite(id: string): Promise<{ id: string; clearedUsers: number }> {
  const { data } = await apiClient.delete(`/hr/worksites/${id}`);
  return data;
}

/** 사업장 변경 이력 한 줄 (사업장 v3 · `GET /hr/worksites/:id/history`). */
export interface WorksiteHistoryEntryDto {
  id: string;
  at: string;
  /** `hr.worksite.saved`(만듦 · 수정) · `hr.worksite.assigned`(이 사업장으로 옮김) */
  eventType: string;
  /** 계약 docs/api/worksites-contract-2026-09-14.md — created(만듦) · updated(수정) · moved(이 사업장으로 옮김). 이름표는 이것으로 고른다. */
  kind?: 'created' | 'updated' | 'moved';
  actorName: string | null;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
  movedCount?: number | null;
}

/** 최신순 · 커서로 더 읽는다. 옛 서버는 라우트가 없어 404. */
export async function getWorksiteHistory(
  id: string, params?: { limit?: number; cursor?: string },
): Promise<{ entries: WorksiteHistoryEntryDto[]; nextCursor: string | null }> {
  const { data } = await apiClient.get(`/hr/worksites/${encodeURIComponent(id)}/history`, { params });
  return { entries: data?.entries ?? [], nextCursor: data?.nextCursor ?? null };
}

/** 빈 근무지(`null`)는 **비우기** — 본사로 되돌린다. */
export async function assignWorksite(userIds: string[], worksiteId: string | null): Promise<{ moved: number }> {
  const { data } = await apiClient.put('/hr/worksites/assign', { userIds, worksiteId });
  return data;
}
